import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { buildConsoleRequestHeaders } from '../utils/backendRequest';

const ApiKeysContext = createContext();

function apiKeysRequestHeaders(token, user) {
  return buildConsoleRequestHeaders(token, user);
}

function clientCoreBaseUrl() {
  return process.env.REACT_APP_CLIENT_CORE_API_URL || '';
}

export function ApiKeysProvider({ children }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const refreshInFlightRef = useRef(false);

  const refreshApiKeys = useCallback(async ({ limit = 100, offset = 0, companyId } = {}) => {
    if (refreshInFlightRef.current) {
      return { items: apiKeys, total };
    }

    refreshInFlightRef.current = true;
    const token = localStorage.getItem('token');
    if (!token) {
      setApiKeys([]);
      setTotal(0);
      setError('apiKeysMissingToken');
      refreshInFlightRef.current = false;
      return { items: [], total: 0 };
    }

    try {
      setLoading(true);
      setError(null);
      const url = new URL(`${clientCoreBaseUrl()}/v1/clients/api-keys`, window.location.origin);
      url.searchParams.set('limit', String(limit));
      url.searchParams.set('offset', String(offset));
      if (companyId) {
        url.searchParams.set('company_id', String(companyId));
      }

      const res = await fetch(url.toString(), {
        headers: apiKeysRequestHeaders(token, user),
      });

      if (res.ok) {
        const data = await res.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        const count = Number.isFinite(data?.total) ? data.total : items.length;
        setApiKeys(items);
        setTotal(count);
        return { items, total: count };
      }

      setApiKeys([]);
      setTotal(0);
      setError('apiKeysLoadFailed');
      return { items: [], total: 0 };
    } catch (err) {
      console.error('Failed to load API keys:', err);
      setApiKeys([]);
      setTotal(0);
      setError('apiKeysLoadFailed');
      return { items: [], total: 0 };
    } finally {
      setLoading(false);
      refreshInFlightRef.current = false;
    }
  }, [user]);

  const createApiKey = useCallback(async (payload) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('apiKeysMissingToken');
      return { ok: false };
    }

    try {
      setSaving(true);
      setError(null);
      const res = await fetch(`${clientCoreBaseUrl()}/v1/clients/api-keys`, {
        method: 'POST',
        headers: {
          ...apiKeysRequestHeaders(token, user),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload || {}),
      });

      if (res.ok) {
        return { ok: true, data: await res.json() };
      }

      setError('apiKeysSaveFailed');
      return { ok: false, status: res.status };
    } catch (err) {
      console.error('Failed to create API key:', err);
      setError('apiKeysSaveFailed');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  }, [user]);

  const getApiKey = useCallback(async (keyId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      return { ok: false };
    }

    try {
      const res = await fetch(
        `${clientCoreBaseUrl()}/v1/clients/api-keys/${encodeURIComponent(keyId)}`,
        {
          headers: apiKeysRequestHeaders(token, user),
        },
      );

      if (res.ok) {
        return { ok: true, data: await res.json() };
      }

      return { ok: false, status: res.status };
    } catch (err) {
      console.error('Failed to load API key:', err);
      return { ok: false };
    }
  }, [user]);

  const updateApiKey = useCallback(async (keyId, payload) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('apiKeysMissingToken');
      return { ok: false };
    }

    try {
      setSaving(true);
      setError(null);
      const res = await fetch(
        `${clientCoreBaseUrl()}/v1/clients/api-keys/${encodeURIComponent(keyId)}`,
        {
          method: 'PATCH',
          headers: {
            ...apiKeysRequestHeaders(token, user),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload || {}),
        },
      );

      if (res.ok) {
        return { ok: true, data: await res.json() };
      }

      setError('apiKeysSaveFailed');
      return { ok: false, status: res.status };
    } catch (err) {
      console.error('Failed to update API key:', err);
      setError('apiKeysSaveFailed');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  }, [user]);

  const deleteApiKey = useCallback(async (keyId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('apiKeysMissingToken');
      return { ok: false };
    }

    try {
      setSaving(true);
      setError(null);
      const res = await fetch(
        `${clientCoreBaseUrl()}/v1/clients/api-keys/${encodeURIComponent(keyId)}`,
        {
          method: 'DELETE',
          headers: apiKeysRequestHeaders(token, user),
        },
      );

      if (res.ok || res.status === 204) {
        return { ok: true };
      }

      setError('apiKeysDeleteFailed');
      return { ok: false, status: res.status };
    } catch (err) {
      console.error('Failed to delete API key:', err);
      setError('apiKeysDeleteFailed');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  }, [user]);

  return (
    <ApiKeysContext.Provider
      value={{
        apiKeys,
        total,
        refreshApiKeys,
        createApiKey,
        getApiKey,
        updateApiKey,
        deleteApiKey,
        loading,
        saving,
        error,
        setError,
      }}
    >
      {children}
    </ApiKeysContext.Provider>
  );
}

export function useApiKeys() {
  return useContext(ApiKeysContext);
}
